import React from "react";
import { ArrowUpRight, BriefcaseBusiness, CalendarCheck, Clock, LucideIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import CheckboxNew from "../../components/Core/Checkbox/CheckboxNew";
import UserCustomers, { NotificationSettings } from "../../models/UserCustomers";
import { customerRoute } from "../../utils/route.utils";

interface UserCustomerRowProps {
	customer: UserCustomers;
	onRemove: (id: string) => void;
	onNotificationUpdate: (id: string, notificationSetting: NotificationSettings) => void;
	/** Disable the remove action (e.g. when this is the user's last customer). */
	removeDisabled?: boolean;
}

export const NOTIFICATION_TYPES: {
	key: keyof NotificationSettings;
	labelKey: string;
	Icon: LucideIcon;
}[] = [
	{
		key: "jobConfirmation",
		labelKey: "customers.multipleCustomers.notificationTypes.booking",
		Icon: CalendarCheck,
	},
	{
		key: "timeReportSummery",
		labelKey: "customers.multipleCustomers.notificationTypes.timeReport",
		Icon: Clock,
	},
	{
		key: "jobReport",
		labelKey: "customers.multipleCustomers.notificationTypes.jobReport",
		Icon: BriefcaseBusiness,
	},
];

/**
 * The label for one notification type — small icon + text. Shared between the
 * relation rows and the select-customer dialog so the two stay in sync.
 */
export const NotificationTypeLabel: React.FC<{ labelKey: string; Icon: LucideIcon }> = ({
	labelKey,
	Icon,
}) => {
	const { t } = useTranslation();

	return (
		<span className="flex items-center gap-1.5">
			<Icon size={14} className="shrink-0 text-gray-400" />
			{t(labelKey as any)}
		</span>
	);
};

/**
 * One customer relation on the customer-user form's "Kunder" card — customer
 * name/number, a checkbox per notification type and a remove action. Toggling
 * a notification or removing calls straight out; persistence is the parent's
 * concern.
 */
const UserCustomerRow: React.FC<UserCustomerRowProps> = ({
	customer,
	onRemove,
	onNotificationUpdate,
	removeDisabled,
}) => {
	const { t } = useTranslation();

	const settings: NotificationSettings = {
		jobConfirmation: !!customer.notificationSetting?.jobConfirmation,
		timeReportSummery: !!customer.notificationSetting?.timeReportSummery,
		jobReport: !!customer.notificationSetting?.jobReport,
	};

	return (
		<div className="rounded-md border border-gray-200 bg-white px-3 py-2.5">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<Link
						to={customerRoute(customer.id)}
						className="group flex items-center gap-0.5 text-[15px] font-medium text-gray-900 hover:text-brand hover:underline"
					>
						<span className="truncate">{customer.name}</span>
						<ArrowUpRight
							size={14}
							className="shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 group-hover:text-brand"
						/>
					</Link>
					<div className="text-xs text-gray-500">#{customer.customerNumber}</div>
				</div>

				<button
					type="button"
					title={t("REMOVE")}
					disabled={removeDisabled}
					onClick={() => onRemove(customer.id)}
					className="-mr-1 -mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:pointer-events-none disabled:opacity-40"
				>
					<X size={15} />
				</button>
			</div>

			<div className="mt-3 flex flex-col gap-2">
				{NOTIFICATION_TYPES.map(({ key, labelKey, Icon }) => (
					<CheckboxNew
						key={key}
						checked={settings[key]}
						onCheckedChange={(checked) =>
							onNotificationUpdate(customer.id, { ...settings, [key]: checked })
						}
						label={<NotificationTypeLabel labelKey={labelKey} Icon={Icon} />}
					/>
				))}
			</div>
		</div>
	);
};

export default UserCustomerRow;
